// SPDX-License-Identifier: AGPL-3.0-or-later

import type {
	CompanionInsightKind,
	CompanionRecording,
	CompanionSessionDetailResponse,
	CompanionSessionSummary,
} from '@app/features/companion/commands/CompanionCommands';
import * as CompanionCommands from '@app/features/companion/commands/CompanionCommands';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import {i18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {makeAutoObservable, runInAction} from 'mobx';

const FAILED_TO_LOAD_SESSIONS_DESCRIPTOR = msg({
	message: 'Failed to load companion sessions',
	comment: 'Generic error when the companion history session list cannot be loaded.',
});
const FAILED_TO_LOAD_DETAIL_DESCRIPTOR = msg({
	message: 'Failed to load companion session',
	comment: 'Generic error when a companion history session detail cannot be loaded.',
});
const FAILED_TO_LOAD_RECORDINGS_DESCRIPTOR = msg({
	message: 'Failed to load recordings',
	comment: 'Generic error when companion session recordings cannot be loaded.',
});
const FAILED_TO_GENERATE_INSIGHT_DESCRIPTOR = msg({
	message: 'Failed to generate AI analysis',
	comment: 'Generic error when generating companion session summary, bookmarks, or action items fails.',
});
const FAILED_TO_DELETE_SESSION_DESCRIPTOR = msg({
	message: 'Failed to delete companion record',
	comment: 'Generic error when deleting a companion session record fails.',
});

export class CompanionRecordsState {
	sessions: Array<CompanionSessionSummary> = [];
	selectedSessionId: string | null = null;
	detail: CompanionSessionDetailResponse | null = null;
	recordings: Array<CompanionRecording> = [];
	isListLoading = false;
	isDetailLoading = false;
	isRecordingsLoading = false;
	listError: string | null = null;
	detailError: string | null = null;
	recordingsError: string | null = null;
	generatingInsightKind: CompanionInsightKind | null = null;
	insightError: string | null = null;
	isDeletingSession = false;
	deleteError: string | null = null;
	private sessionsLoaded = false;
	private sessionsRequestToken = 0;
	private detailRequestToken = 0;
	private recordingsRequestToken = 0;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	get isDetailView(): boolean {
		return this.selectedSessionId !== null;
	}

	get contentKey(): string {
		return this.selectedSessionId ? `companion-records-detail-${this.selectedSessionId}` : 'companion-records-main';
	}

	get selectedSession(): CompanionSessionSummary | null {
		if (this.detail && this.detail.session.session_id === this.selectedSessionId) return this.detail.session;
		return this.sessions.find((session) => session.session_id === this.selectedSessionId) ?? null;
	}

	async loadSessions(force = false): Promise<void> {
		if (this.isListLoading || (this.sessionsLoaded && !force)) return;
		const token = ++this.sessionsRequestToken;
		this.isListLoading = true;
		this.listError = null;
		try {
			const result = await CompanionCommands.listSessions();
			runInAction(() => {
				if (token !== this.sessionsRequestToken) return;
				this.sessions = result.sessions;
				this.sessionsLoaded = true;
			});
		} catch (error) {
			runInAction(() => {
				if (token !== this.sessionsRequestToken) return;
				this.listError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(FAILED_TO_LOAD_SESSIONS_DESCRIPTOR);
			});
		} finally {
			runInAction(() => {
				if (token === this.sessionsRequestToken) this.isListLoading = false;
			});
		}
	}

	async navigateToDetail(sessionId: string): Promise<void> {
		if (this.selectedSessionId !== sessionId) {
			this.detailRequestToken += 1;
			this.recordingsRequestToken += 1;
			this.detail = null;
			this.recordings = [];
			this.isDetailLoading = false;
			this.isRecordingsLoading = false;
		}
		this.selectedSessionId = sessionId;
		this.detailError = null;
		this.recordingsError = null;
		this.insightError = null;
		this.deleteError = null;
		await Promise.all([this.loadDetail(sessionId), this.loadRecordings(sessionId)]);
	}

	navigateToList(): void {
		this.detailRequestToken += 1;
		this.recordingsRequestToken += 1;
		this.selectedSessionId = null;
		this.detail = null;
		this.recordings = [];
		this.detailError = null;
		this.recordingsError = null;
		this.insightError = null;
		this.deleteError = null;
		this.generatingInsightKind = null;
		this.isDeletingSession = false;
		this.isDetailLoading = false;
		this.isRecordingsLoading = false;
	}

	async loadDetail(sessionId: string): Promise<void> {
		const token = ++this.detailRequestToken;
		this.isDetailLoading = true;
		this.detailError = null;
		try {
			const result = await CompanionCommands.getSession(sessionId);
			runInAction(() => {
				if (token !== this.detailRequestToken || this.selectedSessionId !== sessionId) return;
				this.detail = result;
			});
		} catch (error) {
			runInAction(() => {
				if (token !== this.detailRequestToken || this.selectedSessionId !== sessionId) return;
				this.detailError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(FAILED_TO_LOAD_DETAIL_DESCRIPTOR);
			});
		} finally {
			runInAction(() => {
				if (token === this.detailRequestToken) this.isDetailLoading = false;
			});
		}
	}

	async loadRecordings(sessionId: string): Promise<void> {
		const token = ++this.recordingsRequestToken;
		this.isRecordingsLoading = true;
		this.recordingsError = null;
		try {
			const result = await CompanionCommands.listRecordings(sessionId);
			runInAction(() => {
				if (token !== this.recordingsRequestToken || this.selectedSessionId !== sessionId) return;
				this.recordings = result.recordings;
			});
		} catch (error) {
			runInAction(() => {
				if (token !== this.recordingsRequestToken || this.selectedSessionId !== sessionId) return;
				this.recordingsError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(FAILED_TO_LOAD_RECORDINGS_DESCRIPTOR);
			});
		} finally {
			runInAction(() => {
				if (token === this.recordingsRequestToken) this.isRecordingsLoading = false;
			});
		}
	}

	async generateInsight(sessionId: string, kind: CompanionInsightKind): Promise<boolean> {
		if (this.generatingInsightKind !== null) return false;
		this.generatingInsightKind = kind;
		this.insightError = null;
		try {
			const result = await CompanionCommands.generateSessionInsight(sessionId, kind);
			runInAction(() => {
				if (this.detail && this.detail.session.session_id === sessionId) {
					this.detail = {
						...this.detail,
						session: {
							...this.detail.session,
							summary: result.summary ?? this.detail.session.summary,
						},
						bookmarks: result.bookmarks,
						action_items: result.action_items,
					};
				}
				const idx = this.sessions.findIndex((item) => item.session_id === sessionId);
				if (idx >= 0 && result.summary) {
					this.sessions[idx] = {...this.sessions[idx]!, summary: result.summary};
				}
			});
			return true;
		} catch (error) {
			runInAction(() => {
				if (this.selectedSessionId !== sessionId) return;
				this.insightError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(FAILED_TO_GENERATE_INSIGHT_DESCRIPTOR);
			});
			return false;
		} finally {
			runInAction(() => {
				this.generatingInsightKind = null;
			});
		}
	}

	async toggleActionItem(sessionId: string, itemId: string, completed: boolean): Promise<boolean> {
		const previousItems = this.detail?.action_items;
		if (this.detail && this.detail.session.session_id === sessionId && this.detail.action_items) {
			this.detail = {
				...this.detail,
				action_items: this.detail.action_items.map((item) => (item.id === itemId ? {...item, completed} : item)),
			};
		}
		this.insightError = null;
		try {
			const updated = await CompanionCommands.toggleActionItem(sessionId, itemId, completed);
			runInAction(() => {
				if (this.detail && this.detail.session.session_id === sessionId && this.detail.action_items) {
					this.detail = {
						...this.detail,
						action_items: this.detail.action_items.map((item) => (item.id === itemId ? updated : item)),
					};
				}
			});
			return true;
		} catch (error) {
			runInAction(() => {
				if (this.detail && this.detail.session.session_id === sessionId && previousItems) {
					this.detail = {...this.detail, action_items: previousItems};
				}
				this.insightError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(FAILED_TO_GENERATE_INSIGHT_DESCRIPTOR);
			});
			return false;
		}
	}

	async deleteSession(sessionId: string): Promise<boolean> {
		if (this.isDeletingSession) return false;
		this.isDeletingSession = true;
		this.deleteError = null;
		try {
			await CompanionCommands.deleteSession(sessionId);
			runInAction(() => {
				this.sessions = this.sessions.filter((item) => item.session_id !== sessionId);
				if (this.selectedSessionId === sessionId) {
					this.navigateToList();
				}
			});
			return true;
		} catch (error) {
			runInAction(() => {
				this.deleteError =
					CompanionCommands.getCompanionCommandErrorMessage(error) ?? i18n._(FAILED_TO_DELETE_SESSION_DESCRIPTOR);
			});
			return false;
		} finally {
			runInAction(() => {
				this.isDeletingSession = false;
			});
		}
	}

	reset(): void {
		this.sessionsRequestToken += 1;
		this.detailRequestToken += 1;
		this.recordingsRequestToken += 1;
		this.sessions = [];
		this.selectedSessionId = null;
		this.detail = null;
		this.recordings = [];
		this.isListLoading = false;
		this.isDetailLoading = false;
		this.isRecordingsLoading = false;
		this.listError = null;
		this.detailError = null;
		this.recordingsError = null;
		this.generatingInsightKind = null;
		this.insightError = null;
		this.isDeletingSession = false;
		this.deleteError = null;
		this.sessionsLoaded = false;
	}
}

const companionRecordsState = new CompanionRecordsState();
AccountScopedWork.registerCancellation(() => companionRecordsState.reset());

export default companionRecordsState;
